import React, { useState } from 'react';
import { Send, Loader, Mail, AlertTriangle } from 'lucide-react';
import { sendEmail, sendTemplatedEmail } from '../services/emailService';

const EmailTest: React.FC = () => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success?: boolean; message?: string; error?: string } | null>(null);
  const [template, setTemplate] = useState<string>('custom');
  const [name, setName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    try {
      if (template === 'custom') {
        await sendEmail(to, subject, content);
      } else {
        await sendTemplatedEmail(to, template as any, template === 'welcome' ? name : {
          location: 'Test Location',
          score: 85
        });
      }

      setResult({ success: true, message: 'Email sent successfully!' });
    } catch (error) {
      setResult({ success: false, error: 'Failed to send email' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <Mail className="mr-2 text-primary" />
          Test Email Function
        </h2>

        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="text-blue-500 mr-2 mt-1" size={20} />
            <div>
              <h3 className="font-semibold text-blue-500">Testing Guidelines</h3>
              <p className="text-sm text-blue-500/80">
                Use this form to test email functionality. You can either send a custom email or try our pre-built templates.
              </p>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Template:</label>
            <select
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              className="w-full p-2 bg-card border border-border rounded-lg"
            >
              <option value="custom">Custom Email</option>
              <option value="welcome">Welcome Email</option>
              <option value="locationAlert">Location Alert</option>
              <option value="weeklyDigest">Weekly Digest</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">To:</label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full p-2 bg-card border border-border rounded-lg"
              required
            />
          </div>

          {template === 'welcome' && (
            <div>
              <label className="block text-sm font-medium mb-1">Name:</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 bg-card border border-border rounded-lg"
                required
              />
            </div>
          )}

          {template === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Subject:</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full p-2 bg-card border border-border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Content:</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full p-2 bg-card border border-border rounded-lg h-32"
                  required
                />
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader className="animate-spin mr-2" size={20} />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2" size={20} />
                Send Test Email
              </>
            )}
          </button>
        </form>

        {result && (
          <div className={`mt-4 p-4 rounded-lg ${result.success ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
            {result.success ? result.message : result.error}
          </div>
        )}
      </div>

      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-4">Available Templates</h3>
        <div className="space-y-4">
          <div className="p-4 border border-border rounded-lg">
            <h4 className="font-medium">Welcome Email</h4>
            <p className="text-sm text-muted-foreground">Sent to new users when they sign up</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <h4 className="font-medium">Location Alert</h4>
            <p className="text-sm text-muted-foreground">Notifies users about new matching locations</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <h4 className="font-medium">Weekly Digest</h4>
            <p className="text-sm text-muted-foreground">Weekly summary of market insights and updates</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTest;